export type Ceremony = { ceremonyId: string, options: any }
export type SignInOptions = { exists: boolean, hasPasskey: boolean } & Partial<Ceremony>

export class PasskeySignInError extends Error {}

const decode = (value: string): ArrayBuffer => {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
    const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='))
    return Uint8Array.from(binary, c => c.charCodeAt(0)).buffer
}

const encode = (value: ArrayBuffer): string => {
    const bytes = new Uint8Array(value)
    let binary = ''
    for (let i = 0; i < bytes.length; i += 8192) {
        binary += String.fromCharCode(...bytes.subarray(i, i + 8192))
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

const post = async (path: string, body: object = {}) => {
    const response = await fetch(path, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(body)
    })
    const result = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(result.message || 'Passkey request failed.')
    return result
}

export const supportsPasskeys = () => typeof window !== 'undefined' && !!window.PublicKeyCredential

export const registerPasskey = async () => {
    const {ceremonyId, options}: Ceremony = await post('/api/passkeys/registration/options')
    const publicKey: PublicKeyCredentialCreationOptions = {
        ...options,
        challenge: decode(options.challenge),
        user: {...options.user, id: decode(options.user.id)},
        excludeCredentials: (options.excludeCredentials || []).map((item: any) =>
            ({...item, id: decode(item.id)}))
    }
    const credential = await navigator.credentials.create({publicKey}) as PublicKeyCredential | null
    if (!credential) throw new Error('No passkey was created.')
    const response = credential.response as AuthenticatorAttestationResponse & {
        getTransports?: () => string[]
    }
    await post('/api/passkeys/registration/verify', {
        ceremonyId,
        credential: {
            id: credential.id,
            rawId: encode(credential.rawId),
            type: credential.type,
            response: {
                attestationObject: encode(response.attestationObject),
                clientDataJSON: encode(response.clientDataJSON),
                transports: response.getTransports?.() || []
            },
            clientExtensionResults: credential.getClientExtensionResults()
        }
    })
}

export const getSignInOptions = async (email: string): Promise<SignInOptions> =>
    post('/api/passkeys/authentication/options', {email})

export const getDiscoverableSignInOptions = async (): Promise<Ceremony> =>
    post('/api/passkeys/authentication/options')

export const signInWithPasskey = async ({ceremonyId, options}: Ceremony,
    mediation?: CredentialMediationRequirement | 'conditional', signal?: AbortSignal) => {
    const publicKey: PublicKeyCredentialRequestOptions = {
        ...options,
        challenge: decode(options.challenge),
        allowCredentials: (options.allowCredentials || []).map((item: any) =>
            ({...item, id: decode(item.id)}))
    }
    const credential = await navigator.credentials.get(
        {publicKey, mediation, signal} as CredentialRequestOptions
    ) as PublicKeyCredential | null
    if (signal?.aborted) return
    if (!credential) throw new Error('No passkey was selected.')
    const response = credential.response as AuthenticatorAssertionResponse
    try {
        await post('/api/passkeys/authentication/verify', {
            ceremonyId,
            credential: {
                id: credential.id,
                rawId: encode(credential.rawId),
                type: credential.type,
                response: {
                    authenticatorData: encode(response.authenticatorData),
                    clientDataJSON: encode(response.clientDataJSON),
                    signature: encode(response.signature),
                    userHandle: response.userHandle ? encode(response.userHandle) : null
                },
                clientExtensionResults: credential.getClientExtensionResults()
            }
        })
    } catch (error) {
        throw new PasskeySignInError(error instanceof Error ? error.message : 'Passkey sign-in failed.')
    }
}
